import React, { useRef, useState, useEffect, useCallback } from 'react'
import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Con lắc lò xo nằm ngang (Lớp 11 — Dao động): KÉO vật tới x₀ rồi THẢ → biên độ = |x₀|.
// Cấp vận tốc đầu v₀ → biên độ lớn hơn A₀=√(x₀²+(v₀/ω)²). Tăng hệ số cản (ma sát) → dao động TẮT DẦN.
// Phương trình: m·a = −k·x − b·v  →  a = (−k·x − b·v)/m ; ω=√(k/m), T=2π/ω.
const G = 9.8
const W = 660, H = 384
const EQX = 360, CY = 76          // vị trí cân bằng (px) + tâm vật theo trục dọc
const WALLX = 44
const SCALE = 600                 // px / m
const XMAX = 0.25                 // biên kéo tối đa (m)
const WIN = 8                     // cửa sổ thời gian đồ thị (s)

const SpringOscillatorSimulation: React.FC<Props> = ({
  title = 'Con lắc lò xo — kéo & thả (tắt dần)',
  description = 'Kéo vật tới vị trí x₀ rồi thả: biên độ bằng x₀. Cấp vận tốc đầu v₀ → biên độ lớn hơn. Tăng hệ số ma sát/cản → dao động tắt dần.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const [k, setK] = useState(20)     // N/m
  const [m, setM] = useState(1)      // kg
  const [mu, setMu] = useState(0)    // hệ số ma sát (Coulomb) — biên độ giảm TUYẾN TÍNH
  const [v0, setV0] = useState(0)    // m/s
  const [showForce, setShowForce] = useState(true)  // hiện lực kéo về (đàn hồi)
  const [playing, setPlaying] = useState(false)

  const xRef = useRef(0.15)          // li độ hiện tại (m)
  const vRef = useRef(0)
  const x0Ref = useRef(0.15)         // li độ lúc thả
  const a0Ref = useRef(0.15)         // biên độ A₀ lúc thả (để hiển thị)
  const traceRef = useRef<{ t: number; x: number }[]>([])
  const tRef = useRef(0)
  const param = useRef({ k, m, mu })
  const raf = useRef<number | null>(null)
  const lastTs = useRef<number | null>(null)
  const playRef = useRef(false)
  const dragRef = useRef(false)
  const firstRef = useRef(false)

  useEffect(() => { param.current = { k, m, mu } }, [k, m, mu])
  useEffect(() => { playRef.current = playing }, [playing])
  const fireFirst = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    ctx.fillStyle = '#f8fafc'; ctx.fillRect(0, 0, W, H)

    const x = xRef.current
    const bx = EQX + Math.max(-0.46, Math.min(0.46, x)) * SCALE   // tâm vật (px) — kẹp trong khung khi vẽ
    const half = 22

    // Mặt sàn + tường
    ctx.fillStyle = '#e2e8f0'; ctx.fillRect(0, CY + half, W, 6)
    ctx.fillStyle = '#94a3b8'; ctx.fillRect(WALLX - 10, CY - half - 12, 10, half * 2 + 24)

    // Đường VTCB
    ctx.strokeStyle = '#cbd5e1'; ctx.setLineDash([4, 4]); ctx.beginPath(); ctx.moveTo(EQX, CY - half - 16); ctx.lineTo(EQX, CY + half + 6); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#94a3b8'; ctx.font = '11px sans-serif'; ctx.fillText('VTCB', EQX - 14, CY - half - 20)

    // Lò xo (tường → vật)
    const x1 = WALLX, x2 = bx - half, coils = 14
    ctx.strokeStyle = '#64748b'; ctx.lineWidth = 2.5; ctx.beginPath(); ctx.moveTo(x1, CY)
    for (let i = 1; i < coils; i++) { const t = i / coils; const xx = x1 + (x2 - x1) * t; const yy = CY + (i % 2 ? -12 : 12); ctx.lineTo(xx, yy) }
    ctx.lineTo(x2, CY); ctx.stroke()

    // Vật
    const g = ctx.createLinearGradient(0, CY - half, 0, CY + half); g.addColorStop(0, '#7dd3fc'); g.addColorStop(1, '#0284c7')
    ctx.fillStyle = g; ctx.fillRect(bx - half, CY - half, half * 2, half * 2)
    ctx.strokeStyle = '#075985'; ctx.lineWidth = 2; ctx.strokeRect(bx - half, CY - half, half * 2, half * 2)
    ctx.fillStyle = '#fff'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${m}kg`, bx, CY + 4); ctx.textAlign = 'left'

    // Lực kéo về (lực đàn hồi) F = −k·x — hướng về VTCB (checkbox)
    if (showForce && Math.abs(x) > 0.002) {
      const fpx = Math.max(-150, Math.min(150, -k * x * 16)), y0 = CY, d = Math.sign(fpx)
      ctx.strokeStyle = '#16a34a'; ctx.fillStyle = '#16a34a'; ctx.lineWidth = 3
      ctx.beginPath(); ctx.moveTo(bx, y0); ctx.lineTo(bx + fpx, y0); ctx.stroke()
      ctx.beginPath(); ctx.moveTo(bx + fpx, y0); ctx.lineTo(bx + fpx - 9 * d, y0 - 5); ctx.lineTo(bx + fpx - 9 * d, y0 + 5); ctx.closePath(); ctx.fill()
      ctx.font = 'bold 12px sans-serif'; ctx.fillText('F kéo về', bx + fpx / 2 - 24, y0 - 9)
    }

    // ── Đồ thị x(t) ── (đặt số liệu DƯỚI đồ thị, không chồng)
    const gx0 = 56, gw = W - gx0 - 30, gAxisY = 206, gAmp = 70
    const yTop = gAxisY - gAmp - 16, xEnd = gx0 + gw
    ctx.strokeStyle = '#475569'; ctx.fillStyle = '#475569'; ctx.lineWidth = 1.5
    // trục ngang t + mũi tên phải
    ctx.beginPath(); ctx.moveTo(gx0, gAxisY); ctx.lineTo(xEnd, gAxisY); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(xEnd, gAxisY); ctx.lineTo(xEnd - 9, gAxisY - 5); ctx.lineTo(xEnd - 9, gAxisY + 5); ctx.closePath(); ctx.fill()
    // trục đứng x + mũi tên trên
    ctx.beginPath(); ctx.moveTo(gx0, gAxisY + gAmp + 8); ctx.lineTo(gx0, yTop); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(gx0, yTop); ctx.lineTo(gx0 - 5, yTop + 9); ctx.lineTo(gx0 + 5, yTop + 9); ctx.closePath(); ctx.fill()
    ctx.font = 'bold 12px sans-serif'; ctx.fillText('x(cm)', gx0 - 4, yTop - 4); ctx.fillText('t(s)', xEnd - 6, gAxisY + 18)
    // ── Đường biên độ ──
    const A0 = a0Ref.current
    const ampScale = Math.max(XMAX, A0 || XMAX)   // đồ thị tự co giãn để chứa biên độ (kể cả khi có v₀ lớn)
    const tr = traceRef.current
    const tEnd = tRef.current, tStart = Math.max(0, tEnd - WIN)
    const gMap = (p: { t: number; x: number }) => ({
      gx: gx0 + ((p.t - tStart) / WIN) * gw,
      gy: gAxisY - Math.max(-1.1, Math.min(1.1, p.x / ampScale)) * gAmp,
    })
    if (mu > 0 && tr.length > 2) {
      // CÓ MA SÁT Coulomb → biên độ giảm TUYẾN TÍNH. Nối ĐÚNG các đỉnh thật (đỉnh thẳng hàng → đường thẳng),
      // kéo dài từ trục tung (0, ±A₀) và NGOẠI SUY tới thời điểm hiện tại → đường bao chạy THEO THỜI GIAN.
      const dead = (mu * G) / (k / m)
      const ups: { t: number; v: number }[] = [{ t: 0, v: A0 }]
      const downs: { t: number; v: number }[] = [{ t: 0, v: -A0 }]
      for (let i = 1; i < tr.length - 1; i++) {
        const p = tr[i].x, a = tr[i - 1].x, c = tr[i + 1].x
        if (p > a && p >= c) ups.push({ t: tr[i].t, v: p })
        else if (p < a && p <= c) downs.push({ t: tr[i].t, v: p })
      }
      // ngoại suy đoạn cuối tới tEnd để đường bao theo kịp vệt đồ thị
      const extend = (pts: { t: number; v: number }[], sgn: number) => {
        if (pts.length < 2) return
        const b = pts[pts.length - 1], a = pts[pts.length - 2]
        if (b.t < tEnd - 1e-3) {
          const slope = (b.v - a.v) / Math.max(1e-3, b.t - a.t)
          let ve = b.v + slope * (tEnd - b.t)
          ve = sgn > 0 ? Math.max(dead, ve) : Math.min(-dead, ve)
          pts.push({ t: tEnd, v: ve })
        }
      }
      extend(ups, 1); extend(downs, -1)
      const env = (pts: { t: number; v: number }[]) => {
        const w = pts.filter((p) => p.t >= tStart - 1e-3)
        if (w.length < 2) return
        ctx.strokeStyle = 'rgba(244,63,94,0.75)'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]); ctx.beginPath()
        w.forEach((p, k) => {
          const gx = gx0 + ((p.t - tStart) / WIN) * gw
          const gy = gAxisY - Math.max(-1.1, Math.min(1.1, p.v / ampScale)) * gAmp
          k ? ctx.lineTo(gx, gy) : ctx.moveTo(gx, gy)
        })
        ctx.stroke(); ctx.setLineDash([])
      }
      env(ups); env(downs)
    } else {
      // KHÔNG ma sát → 2 đường biên độ A₀ nằm ngang
      const ay = (A0 / ampScale) * gAmp
      ctx.strokeStyle = 'rgba(244,63,94,0.35)'; ctx.setLineDash([5, 4])
      ctx.beginPath(); ctx.moveTo(gx0, gAxisY - ay); ctx.lineTo(xEnd, gAxisY - ay); ctx.moveTo(gx0, gAxisY + ay); ctx.lineTo(xEnd, gAxisY + ay); ctx.stroke(); ctx.setLineDash([])
    }
    // vệt x(t)
    if (tr.length > 1) {
      ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2; ctx.beginPath()
      let started = false
      for (const p of tr) {
        if (p.t < tStart) continue
        const g = gMap(p)
        started ? ctx.lineTo(g.gx, g.gy) : (ctx.moveTo(g.gx, g.gy), started = true)
      }
      ctx.stroke()
    }

    // Số liệu — DƯỚI đồ thị
    const omega = Math.sqrt(k / m), T = (2 * Math.PI) / omega
    ctx.fillStyle = '#0f172a'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText(`ω = √(k/m) = ${omega.toFixed(2)} rad/s · T = ${T.toFixed(2)} s`, gx0, 330)
    ctx.fillStyle = '#dc2626'
    ctx.fillText(`Biên độ A₀ = ${(A0 * 100).toFixed(1)} cm  (x₀ = ${(x0Ref.current * 100).toFixed(1)} cm, v₀ = ${v0} m/s)`, gx0, 354)
    if (mu > 0) { ctx.fillStyle = '#7c3aed'; ctx.fillText('ma sát → tắt dần (biên độ giảm tuyến tính)', xEnd - 290, 330) }
  }, [k, m, mu, v0, showForce])

  const loop = useCallback((ts: number) => {
    if (!playRef.current) return
    if (lastTs.current == null) lastTs.current = ts
    let dt = (ts - lastTs.current) / 1000; lastTs.current = ts; dt = Math.min(dt, 0.03)
    const { k: kk, m: mm, mu: mmu } = param.current
    let x = xRef.current, v = vRef.current
    const w2 = kk / mm, fr = mmu * G        // a = −ω²x − μg·sign(v) (ma sát Coulomb)
    let a = 0, settle = false
    if (Math.abs(v) < 0.02 && w2 * Math.abs(x) <= fr + 1e-6) settle = true        // vào vùng chết
    else if (Math.abs(v) < 0.02) a = -w2 * x + fr * Math.sign(x)                   // thắng ma sát nghỉ → tiếp tục
    else a = -w2 * x - fr * Math.sign(v)                                           // đang chuyển động

    let done = false
    if (settle) {
      x *= 0.82; v = 0                       // ÊM về VTCB rồi dừng (tránh dừng đột ngột)
      if (Math.abs(x) < 0.004) { x = 0; done = true }
    } else {
      v += a * dt; x += v * dt              // KHÔNG kẹp vị trí vật lí — chỉ kẹp khi vẽ vật
    }
    xRef.current = x; vRef.current = v
    tRef.current += dt
    traceRef.current.push({ t: tRef.current, x })
    if (traceRef.current.length > 2000) traceRef.current.shift()
    draw()
    if (done) { setPlaying(false); return }
    if (playRef.current) raf.current = requestAnimationFrame(loop)
  }, [draw])

  useEffect(() => {
    if (playing) { lastTs.current = null; raf.current = requestAnimationFrame(loop) }
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [playing, loop])
  useEffect(() => { if (!playing) draw() }, [draw, playing])

  const launch = () => {
    fireFirst()
    xRef.current = x0Ref.current; vRef.current = v0; tRef.current = 0; traceRef.current = []
    const omega = Math.sqrt(k / m)
    a0Ref.current = Math.sqrt(x0Ref.current * x0Ref.current + (v0 / omega) * (v0 / omega))
    setPlaying(true)
  }

  // Kéo vật để đặt x₀
  const toLogic = (e: React.PointerEvent) => { const r = cv.current!.getBoundingClientRect(); return (e.clientX - r.left) / r.width * W }
  const onDown = (e: React.PointerEvent) => {
    const px = toLogic(e); const bx = EQX + xRef.current * SCALE
    if (Math.abs(px - bx) < 40) { dragRef.current = true; setPlaying(false); fireFirst(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId) }
  }
  const onMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return
    const px = toLogic(e); let x = (px - EQX) / SCALE; x = Math.max(-XMAX, Math.min(XMAX, x))
    xRef.current = x; x0Ref.current = x; a0Ref.current = Math.abs(x); vRef.current = 0; draw()
  }
  const onUp = () => { if (dragRef.current) { dragRef.current = false; launch() } }
  const reset = () => { setPlaying(false); xRef.current = 0; x0Ref.current = 0; a0Ref.current = 0; vRef.current = 0; traceRef.current = []; tRef.current = 0; draw() }

  const sf = <T,>(s: (v: T) => void) => (v: T) => { fireFirst(); s(v) }

  return (
    <div className="rounded-2xl border border-sky-200 bg-white p-4 my-3">
      <h4 className="font-bold text-sky-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
        className="w-full rounded-lg border border-gray-100 bg-slate-50 touch-none cursor-grab active:cursor-grabbing" />
      <div className="flex items-center justify-center gap-2 my-3">
        <button onClick={launch} className="px-4 py-2 rounded-xl bg-sky-600 text-white text-sm font-bold hover:bg-sky-700">▶ Thả / Chạy lại</button>
        <button onClick={() => setPlaying(false)} className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-bold hover:bg-amber-600">⏸ Dừng</button>
        <button onClick={reset} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">↺ Về VTCB</button>
      </div>
      <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer"><input type="checkbox" checked={showForce} onChange={(e) => { fireFirst(); setShowForce(e.target.checked) }} /> <span className="text-emerald-700 font-semibold">Hiện lực kéo về (lực đàn hồi)</span></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Độ cứng k</span><input type="range" min={5} max={50} step={1} value={k} onChange={(e) => sf(setK)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{k} N/m</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Khối lượng m</span><input type="range" min={0.5} max={3} step={0.5} value={m} onChange={(e) => sf(setM)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{m} kg</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Hệ số ma sát μ</span><input type="range" min={0} max={0.3} step={0.01} value={mu} onChange={(e) => sf(setMu)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{mu.toFixed(2)}</span></label>
        <label className="flex items-center gap-2"><span className="w-28 text-gray-600">Vận tốc đầu v₀</span><input type="range" min={0} max={3} step={0.5} value={v0} onChange={(e) => sf(setV0)(Number(e.target.value))} className="flex-1" /><span className="w-16 text-right font-semibold">{v0} m/s</span></label>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Kéo vật ra khỏi VTCB rồi thả tay để dao động. v₀=0 → biên độ đúng bằng x₀; v₀&gt;0 → biên độ lớn hơn. Tăng hệ số cản để thấy biên độ giảm dần (tắt dần).</p>
    </div>
  )
}

export default SpringOscillatorSimulation
